/**
 * ErrorBoundary — Unico componente consolidato per RideConnect.
 * 
 * Sostituisce e unifica i 3 precedenti:
 *   - components/ErrorBoundary.tsx          (❌ rimosso — dead code)
 *   - components/AppErrorBoundary.tsx       (❌ rimosso — confluito qui)
 *   - components/ui/ErrorBoundary.tsx       (✅ consolidato qui)
 *
 * Modalità di utilizzo:
 *   1. FUORI dal Router (main.tsx) → <ErrorBoundary variant="fullpage" />
 *   2. DENTRO il Router (Layout.tsx) → <ErrorBoundary resetKey={pathname} />
 *   3. Intorno a un singolo componente → <ErrorBoundary fallback={<Card/>} />
 *
 * Il resetKey forza il reset dell'errore quando cambia (es. cambio route).
 */
import React from 'react';
import { AlertTriangle, RefreshCcw, AlertCircle } from 'lucide-react';

// ── Props ──────────────────────────────────────────────────────────────────

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** 
   * Chiave di reset: quando cambia, l'ErrorBoundary resetta l'errore.
   * Tipicamente location.pathname (per reset automatico su cambio route).
   */
  resetKey?: string;
  /** Fallback UI personalizzato. Se omesso, usa il fallback default. */
  fallback?: React.ReactNode;
  /**
   * Variante di rendering del fallback:
   * - 'inline'   (default) — centrato nella pagina, leggero, con dettagli espandibili
   * - 'fullpage' — a schermo intero, per boundary esterno al Router (es. main.tsx)
   */
  variant?: 'inline' | 'fullpage';
}

// ── State ──────────────────────────────────────────────────────────────────

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorKey: string;
}

// ── Componente ─────────────────────────────────────────────────────────────

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorKey: props.resetKey ?? '' };
  }

  // Reset automatico su cambio resetKey (es. navigazione a nuova route)
  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (props.resetKey !== undefined && props.resetKey !== state.errorKey) {
      return { hasError: false, error: null, errorKey: props.resetKey };
    }
    return null;
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught:', error.message, errorInfo.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  // ── Fallback UI in base alla variante ──────────────────────────────────

  renderFallback() {
    // Fallback personalizzato
    if (this.props.fallback) return this.props.fallback;

    const errorMsg = this.state.error?.message || 'Errore sconosciuto';

    // Variante fullpage (usata FUORI dal Router, in main.tsx)
    if (this.props.variant === 'fullpage') {
      return (
        <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
          <div className="max-w-md w-full rounded-3xl border border-neutral-800 bg-neutral-950 p-6">
            <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
              <AlertCircle size={28} className="text-red-400" />
            </div>
            <div className="text-xl font-extrabold mb-1">Ops, qualcosa è andato storto</div>
            <div className="text-sm text-neutral-400 mb-2">
              Ricarica l'app. Se il problema persiste, contatta il supporto.
            </div>
            <div className="text-[11px] text-neutral-600 break-words mb-5">{errorMsg}</div>
            <button
              onClick={() => window.location.reload()}
              className="w-full rounded-2xl bg-blue-600 hover:bg-blue-500 px-4 py-3 font-extrabold text-sm active:scale-95 transition-all"
            >
              Ricarica
            </button>
          </div>
        </div>
      );
    }

    // Variante inline (default, usata DENTRO il Router)
    return (
      <div className="flex items-center justify-center min-h-[60vh] p-6">
        <div className="text-center max-w-sm">
          <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
            <AlertTriangle size={32} className="text-red-400" />
          </div>
          <h2 className="text-white font-bold text-lg mb-2">Qualcosa è andato storto</h2>
          <p className="text-neutral-400 text-sm mb-4">
            Si è verificato un errore imprevisto. Puoi riprovare a caricare la pagina.
          </p>
          <button
            onClick={this.handleRetry}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-brand-500 hover:bg-brand-400 text-white font-bold text-sm active:scale-95 transition-all"
          >
            <RefreshCcw size={16} />
            Riprova
          </button>
          <details className="mt-4 text-left">
            <summary className="text-neutral-600 text-xs cursor-pointer hover:text-neutral-400">
              Dettagli tecnici
            </summary>
            <pre className="mt-2 p-3 rounded-xl bg-gray-900 text-red-400 text-[10px] overflow-auto max-h-32">
              {errorMsg}
            </pre>
          </details>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.hasError) return this.renderFallback();
    return this.props.children;
  }
}

// ── Named export per compatibilità con main.tsx ────────────────────────────
// AppErrorBoundarySimple era l'export usato da main.tsx fuori dal Router.
// Ora è semplicemente <ErrorBoundary variant="fullpage" />
export { ErrorBoundary as AppErrorBoundarySimple };
